import React, { FC } from 'react';
import {
    SubmitErrorHandler,
    SubmitHandler,
    useForm
} from 'react-hook-form/dist/index.ie11';

import { Text, FieldMessage, Label } from '@blueprint/ui';
import triggerTracking from '@blueprint/tracking';
import { useDispatch } from 'react-redux';
import { setLoggedIn, setUser } from 'reducers/user';

import registrationAndSignInTranslations from 'public/locales/en/registration-and-sign-in.json';
import { getTranslator } from 'shared/i18n/getTranslator';
import useRegistration from './../../hooks/useRegistration';
import RegistrationFormData from './../../interfaces/RegistrationFormData';
import { trackAuthSubmit } from './../../tracking';
import { EMAIL_REGEX, PASSWORD_MIN_LENGTH } from './../../validation/rules';

import {
    FormSection,
    RequiredIndicator,
    ReassuranceText,
    RegisterButton,
    FieldContainer,
    RequiredContainer,
    SignInErrorMessage
} from './../../AuthModal.styles';
import validationDecoration from 'shared/helpers/validationDecoration';
import { RadioGroup, RadioButton } from 'shared/components';
import { SocialAuthProps } from '../../AuthModal.types';
import {
    transformFormData,
    hasNthErrors
} from 'shared/components/AuthModal/utils';
import { InputPassword, TextField } from '@blueprint/text-input';
import { useSocialSignIn } from '../SignIn/hooks/useSocialSignIn/useSocialSignIn';
import SocialAuth from '../SignIn/SocialAuth';
import { User } from 'graphql.generated';
import ErrorAlert from '../../ErrorAlert/ErrorAlert';
import { AboutYou } from '../../common/AboutYou';

const translate = getTranslator(registrationAndSignInTranslations);
const serviceError = translate('serviceError');
const required = translate('required');
const detailsEmailFieldLabel = translate('detailsSection.emailFieldLabel');
const detailsEmailFieldValidation = translate(
    'detailsSection.emailFieldValidation'
);
const detailsPasswordFieldLabel = translate(
    'detailsSection.passwordFieldLabel'
);
const detailsPasswordFieldGuidance = translate(
    'detailsSection.passwordFieldGuidance'
);
const termsUnsubscribe = translate('terms.youMayUnsubscribe');
const registerButton = translate('registerButton');
const socialRegisterTextTn = translate('socialRegister.copy');
const googleRegisterTextTn = translate('socialRegister.google');
const facebookRegisterTextTn = translate('socialRegister.facebook');

const socialCopy = {
    cta: socialRegisterTextTn,
    facebook: facebookRegisterTextTn,
    google: googleRegisterTextTn
};

const RegisterForm: FC<
    Pick<
        SocialAuthProps,
        | 'close'
        | 'actionAfterAuth'
        | 'resetActionAfterAuth'
        | 'isSSOFeatureEnabled'
        | 'onSocialAuth'
    >
> = ({
    close,
    actionAfterAuth,
    resetActionAfterAuth = () => null,
    isSSOFeatureEnabled = true,
    onSocialAuth
}) => {
    const {
        register,
        handleSubmit,
        errors,
        setError,
        formState,
        reset,
        trigger
    } = useForm<RegistrationFormData>({
        mode: 'onBlur',
        reValidateMode: 'onBlur'
    });

    const { registerUser, generalError } = useRegistration(setError, close);
    const reduxDispatch = useDispatch();

    const onRegisterSuccess = async () => {
        reduxDispatch(setLoggedIn(true));
        await actionAfterAuth?.action();
        resetActionAfterAuth();
        close();
    };

    const onSocialAuthSuccess = async (data?: User) => {
        if (data) {
            reduxDispatch(setUser(data));
        }

        if (onSocialAuth) {
            onSocialAuth(data);
        } else {
            close();
        }
    };

    const [facebookError, onFacebookRegister] = useSocialSignIn({
        type: 'facebook',
        actionAfterAuth: {
            action: onSocialAuthSuccess,
            eventLabel: actionAfterAuth?.eventLabel || 'register_modal',
            segment: actionAfterAuth?.segment
        }
    });

    const [googleError, onGoogleRegister] = useSocialSignIn({
        type: 'google',
        actionAfterAuth: {
            action: onSocialAuthSuccess,
            eventLabel: actionAfterAuth?.eventLabel || 'register_modal',
            segment: actionAfterAuth?.segment
        }
    });

    const onFormSubmit: SubmitHandler<RegistrationFormData> = async (
        formData
    ) => {
        const payload = await registerUser(transformFormData(formData));
        if (!payload?.data?.registerUser) {
            return;
        }

        if (payload.data.registerUser.success) {
            onRegisterSuccess();
        }

        trackAuthSubmit({
            payload: payload.data.registerUser,
            formData,
            eventLabel: actionAfterAuth?.eventLabel,
            segment: actionAfterAuth?.segment
        });
    };
    const onFormSubmitError: SubmitErrorHandler<RegistrationFormData> = (
        errors
    ) => {
        if (
            errors.consent__insights__24 &&
            hasNthErrors({ expectedErrors: 1, formErrors: errors })
        ) {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'registration',
                eventAction: 'failure_must_select_opt_in',
                eventLabel: actionAfterAuth?.eventLabel
            });
        }
    };

    const onSubmit = handleSubmit(onFormSubmit, onFormSubmitError);

    React.useEffect(() => {
        // Reset form when Safari / Firefox read the previous page from the Back-Forward Cache
        window.addEventListener('pageshow', function () {
            reset();
        });

        reset();
    }, []);

    return (
        <>
            <form
                onSubmit={onSubmit}
                noValidate
                data-testid="auth-modal-registration-form"
                method="post"
            >
                {generalError && (
                    <FieldMessage
                        data-testid="general-error"
                        variant="invalid"
                        message={serviceError}
                    />
                )}

                <FormSection>
                    <FieldContainer>
                        <Label htmlFor="input-email-address" isRequired>
                            {detailsEmailFieldLabel}
                        </Label>
                        <RequiredContainer>
                            <Text size="small">
                                <RequiredIndicator>*</RequiredIndicator>
                                {required}
                            </Text>
                        </RequiredContainer>
                        <TextField
                            id="input-email-address"
                            name="email"
                            type="email"
                            autoComplete="new-email"
                            validationStatus={validationDecoration<RegistrationFormData>(
                                'email',
                                errors,
                                formState.touched
                            )}
                            validationErrorMessage={
                                errors.email &&
                                ((errors.email.message as string) ||
                                    detailsEmailFieldValidation)
                            }
                            ref={register({
                                required: true,
                                pattern: EMAIL_REGEX
                            })}
                            data-testid="email-field"
                        />
                    </FieldContainer>
                    <FieldContainer>
                        <Label isRequired htmlFor="input-password">
                            {detailsPasswordFieldLabel}
                        </Label>
                        <InputPassword
                            id="input-password"
                            name="password"
                            autoComplete="new-password"
                            helpText={detailsPasswordFieldGuidance}
                            validationStatus={validationDecoration<RegistrationFormData>(
                                'password',
                                errors,
                                formState.touched
                            )}
                            validationErrorMessage={
                                errors.password &&
                                ((errors.password.message as string) ||
                                    detailsPasswordFieldGuidance)
                            }
                            ref={register({
                                required: true,
                                minLength: PASSWORD_MIN_LENGTH
                            })}
                            data-testid="password-field"
                        />
                    </FieldContainer>
                </FormSection>

                <FormSection>
                    <FieldContainer>
                        <AboutYou dropDownRef={register} />
                    </FieldContainer>
                </FormSection>
                <FormSection>
                    <RadioGroup
                        legend={translate('emailPreferences.legend')}
                        description={translate('emailPreferences.description')}
                        radioGroupName="consent__insights__24"
                        errorMessage={
                            errors.consent__insights__24 &&
                            translate('emailPreferences.errorMessage')
                        }
                        onClick={() => trigger('consent__insights__24')}
                    >
                        <RadioButton
                            value="true"
                            id="yes"
                            activeTextColor="white"
                            label={translate('emailPreferences.yes')}
                            ref={register({ required: true })}
                        />
                        <RadioButton
                            value=""
                            id="no"
                            label={translate('emailPreferences.no')}
                            activeTextColor="white"
                            activeBackgroundColor="function-error"
                            ref={register({ required: true })}
                        />
                    </RadioGroup>
                </FormSection>
                <ReassuranceText
                    size="secondary"
                    color="grey-slate"
                    isBold
                    data-testid="unsubscribe-text"
                >
                    {termsUnsubscribe}
                </ReassuranceText>

                <RegisterButton
                    variant="primary"
                    isFullWidth
                    data-testid="register-button"
                >
                    {registerButton}
                </RegisterButton>
            </form>

            {isSSOFeatureEnabled && (
                <>
                    <ErrorAlert hasError={!!facebookError || !!googleError}>
                        {facebookError !== '' && (
                            <SignInErrorMessage
                                data-testid="facebook-error"
                                variant="invalid"
                                message={facebookError}
                            />
                        )}

                        {googleError !== '' && (
                            <SignInErrorMessage
                                data-testid="google-error"
                                variant="invalid"
                                message={googleError}
                            />
                        )}
                    </ErrorAlert>

                    <SocialAuth
                        onFacebookLogin={onFacebookRegister}
                        onGoogleLogin={onGoogleRegister}
                        copy={socialCopy}
                        type="register"
                    />
                </>
            )}
        </>
    );
};

export default RegisterForm;
